<script setup>
  import { defineAsyncComponent, ref, computed, watch } from 'vue'
  import { text } from '@/plugins/utils'

  import { useRouter } from 'vue-router'
  const router = useRouter()

  // components
  const BaseLoader = defineAsyncComponent (() =>
      import(/* webpackChunkName: "components/base/BaseLoader" */ '@/components/base/BaseLoader.vue')
  );
  const BaseButton = defineAsyncComponent(() =>
    import(/* webpackChunkName: "BaseButton" */ '@/components/base/buttons/BaseButton.vue')
  );
  const Steppers = defineAsyncComponent(() =>
    import(/* webpackChunkName: "Steppers" */ '@/components/base/stepper/StepperMain.vue')
  );
  const StepOne = defineAsyncComponent(() =>
    import(/* webpackChunkName: "StepOne" */ '@/views/cards/CardPage/NewCardPage/steps/StepOne.vue')
  );
  const StepTwo = defineAsyncComponent(() =>
    import(/* webpackChunkName: "StepTwo" */ '@/views/cards/CardPage/NewCardPage/steps/StepTwo.vue')
  );
  const StepThree = defineAsyncComponent(() =>
    import(/* webpackChunkName: "StepThree" */ '@/views/cards/CardPage/NewCardPage/steps/StepThree.vue')
  );
  const StepFour = defineAsyncComponent(() =>
    import(/* webpackChunkName: "StepFour" */ '@/views/cards/CardPage/NewCardPage/steps/StepFour.vue')
  );

  const currentStep = ref(1)
  const steps = ref(4)
  const loading = ref(false)

  const actionBtnText = computed(() => {
    if (currentStep.value === 1) {
      return text('create_card_page.action_btn.next')
    } else if (currentStep.value === 2) {
      return text('create_card_page.action_btn.accept')
    } else {
      return text('create_card_page.action_btn.send')
    }
  })

  function stepBack() {
    if (currentStep.value > 1) {
      currentStep.value--
    } else {
      router.back()
    }
  }
  function stepForward() {
    if (currentStep.value === 3) {
      sendKYC()
    }
    if (currentStep.value === 4) {
      sendConfirmationCode()
    }
    if (currentStep.value !== steps.value) {
      currentStep.value++
    }
  }

  const selectedCurrency = ref(null)
  const selectedIssuer = ref(null)
  const kyc = ref({
    occupation: null,
    salary: null,
    purpose: null,
    volume: null,
    email: null,
    phone: null
  })
  const confirmationCode = ref('')
  const confirmationError = ref(false)

  const issuers = computed(() => {
    if (selectedCurrency.value) {
      return selectedCurrency.value.issuers.map(item => ({ 'value': item }))
    } else {
      return []
    }
  })

  watch(selectedCurrency, () => {
    selectedIssuer.value = issuers.value[0]
  })

  const isValid = computed(() => {
    if (currentStep.value === 1) {
      return selectedCurrency.value && selectedIssuer.value
    }
    if (currentStep.value === 2) {
      return true
    }
    if (currentStep.value === 3) {
      const isValidEmail = (email) => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(email);
      return Object.keys(kyc.value).every(key => !!kyc.value[key]) && isValidEmail(kyc.value.email)
    }
    if (currentStep.value === 4) {
      return true
    }
  })

  import { useStore } from 'vuex'
  const store = useStore()
  const authInfo = computed (() => {
    return store.state.authInfo
  })
  const blankCard = computed(() => {
    const { last_name,  first_name } = authInfo.value
    return {
      "id": '77eb5f0d-1d61-46cd-a3f4-0e98f6de8777',
      "card_number": '4539005101800135',
      "issuer": selectedIssuer.value.value,
      "exp": "08/2028",
      "cvv": 616,
      "holder": `${last_name} ${first_name}`,
      "address": "6655 Williamson Dale",
      "country": "Eritrea",
      "zipcode": "91757-1519",
      "balance": 0,
      "status": "exist",
      "rules": {
        "currency": 'eur',
        "price": 10,
        "min_deposit": 10,
        "max_deposit": 100000,
      }
    }
  })

  const sendKYC = async ()=> {
    loading.value = true

    new Promise((resolve) => {
      setTimeout(() => {
        loading.value = false

        resolve()
      }, 500)
    })
  }

  let codeFromEmail = ref(null)
  function sendCodeToEmail(resetTimer) {
    codeFromEmail.value = Math.floor(1000 + Math.random() * 9000)
    // console.log('codeFromEmail ---> ', codeFromEmail.value)
    resetTimer(30)
  }
  const sendConfirmationCode = async ()=> {
    new Promise((resolve) => {
      confirmationError.value = codeFromEmail.value !== Number(confirmationCode.value)

      if (confirmationError.value) {
        confirmationCode.value = ''
        resolve()
      } else {
        loading.value = true

        setTimeout(() => {
          loading.value = false

          console.log(blankCard.value)
          router.push({ name: 'VerificationCardPage', params: { id: blankCard.value.id } })

          resolve()
        }, 500)
      }
    })
  }
</script>

<template>
  <div class="new-card-page">
    <div class="new-card-page__title">
      <div class="icon" @click="stepBack"> < </div>
      <div class="text"> {{ text('create_card_page.action_btn.title') }} </div>
    </div>
    <div class="stepper-wrapper">
      <Steppers :steps="4" :currentStep="currentStep" />
    </div>
    <div class="new-card-page__body">
      <BaseLoader v-if="loading" style="margin: 20px 0" />
      <template v-else-if="currentStep === 1">
        <StepOne
          v-model:currency="selectedCurrency"
          v-model:issuer="selectedIssuer"
          :issuers="issuers"
        />
      </template>
      <template v-else-if="currentStep === 2">
        <StepTwo :card="blankCard" />
      </template>
      <template v-else-if="currentStep === 3">
        <StepThree :kyc="kyc" />
      </template>
      <template v-else-if="currentStep === 4">
        <StepFour :email="kyc.email" v-model="confirmationCode" :error="confirmationError">
          <template #action="{ timer, resetTimer }">
            <div>
              <span v-if="timer.stopped" style="cursor: pointer" @click="sendCodeToEmail(resetTimer)">
                {{ text('create_card_page.action_btn.send_code') }}
              </span>
              <span v-else>
                {{ text('create_card_page.action_btn.resend_code') }} ({{ timer.label }})
              </span>
            </div>
          </template>
        </StepFour>
      </template>
    </div>
    <BaseButton
      suffix
      arrow
      @click="stepForward"
      :disabled="!isValid"
    >
      <template #label>
        {{ actionBtnText }}
      </template>
    </BaseButton>
  </div>
</template>

<style lang="scss" scoped>
  .new-card-page {
    min-height: 500px;

    display: flex;
    flex-direction: column;
    gap: 12px;

    padding: 24px;

    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;

      margin-bottom: 8px;

      .icon {
        width: 8px;
        flex-shrink: 0;
        cursor: pointer;
      }
      .text {
        width: 100%;

        font-size: 18px;
        font-weight: 500;
        text-align: center;
        transform: translateX(-8px);
        pointer-events: none;
      }
    }
    &__body {
      min-height: 365px;
      margin-bottom: 20px;
      padding-top: 8px;
    }
  }
</style>